import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb } from '@/shared/components/ui/Breadcrumb';

import { useChannelDashboardOverview, useChannelMetrics, useChannelSyncSocket, useSyncChannel } from '../hooks';
import type { MetricsRange } from '../services/channelDashboardApi';
import { AudiencePanel } from '../components/dashboard/AudiencePanel';
import { CDASH_CSS } from '../components/dashboard/dashboardStyles';
import { ActivityTimeline } from '../components/dashboard/ActivityTimeline';
import { DashboardTopBar } from '../components/dashboard/DashboardTopBar';
import { KpiRow } from '../components/dashboard/KpiRow';
import { CommentsSection } from '../components/dashboard/CommentsSection';
import { PostsSection } from '../components/dashboard/PostsSection';
import { ProfileHeader } from '../components/dashboard/ProfileHeader';
import { ReachChartCard } from '../components/dashboard/ReachChartCard';
import { SectionNav, type SectionKey } from '../components/dashboard/SectionNav';
import { SyncSection } from '../components/dashboard/SyncSection';

export const ChannelDashboardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [section, setSection] = useState<SectionKey>('overview');
  const [range, setRange] = useState<MetricsRange>('7d');

  const { data: overview, isLoading, isError } = useChannelDashboardOverview(id);
  const hasInsights = !!overview?.capabilities.includes('get_insights');
  const hasAudience = !!overview?.capabilities.includes('get_audience');
  const { data: metrics = [] } = useChannelMetrics(id, range, hasInsights && (section === 'metrics' || section === 'overview'));
  const syncMutation = useSyncChannel(id);
  const { liveStatus } = useChannelSyncSocket(id);

  const syncStatus = liveStatus ?? overview?.syncState?.syncStatus ?? 'idle';
  const latest = overview?.latestMetrics ?? null;
  const previous = useMemo(() => (metrics.length >= 2 ? metrics[metrics.length - 2] : null), [metrics]);

  // Reseta a seção ao trocar de canal (a mesma instância é reusada quando só o :id muda).
  useEffect(() => {
    setSection('overview');
  }, [id]);

  const channelName = useMemo(() => {
    if (!overview) return '';
    const info = overview.channel.accountInfo;
    return info?.verifiedName || info?.businessName || info?.pushName || overview.channel.name;
  }, [overview]);

  const handleExport = useCallback(() => {
    if (metrics.length === 0) return;
    const header = ['Data', 'Alcance', 'Impressões', 'Engajamento', 'Visitas ao perfil', 'Seguidores', 'Seguindo', 'Publicações'];
    const lines = metrics.map((m) =>
      [m.date, m.reach ?? '', m.impressions ?? '', m.engagement ?? '', m.profileViews ?? '', m.followersCount ?? '', m.followsCount ?? '', m.mediaCount ?? ''].join(','),
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `metricas-${channelName || 'canal'}-${range}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [metrics, channelName, range]);

  return (
    <div className="cdash" data-testid="channel-dashboard-page">
      <style>{CDASH_CSS}</style>

      {/* Toolbar 3-zonas: breadcrumb shared (F25–F31) · período + sync + atualizar (dir) */}
      <header className="cdash-tb" data-testid="cdash-toolbar">
        <Breadcrumb
          items={[
            { label: 'Configurações', onNavigate: () => navigate('/settings'), testid: 'cdash-bc-settings' },
            { label: 'Canais', onNavigate: () => navigate('/settings/channels'), testid: 'cdash-bc-channels' },
            ...(channelName ? [{ label: channelName, testid: 'cdash-bc-current' }] : []),
          ]}
          mobileLegend="Configurações · Canais"
          data-testid="cdash-breadcrumb"
        />

        {overview ? (
          <div className="cdash-tb-right">
            <DashboardTopBar
              syncStatus={syncStatus}
              lastSyncedAt={overview.syncState?.lastSyncedAt}
              range={range}
              onRange={setRange}
              onSync={() => syncMutation.mutate(undefined)}
              syncing={syncMutation.isPending}
              onExport={handleExport}
              canExport={metrics.length > 0}
            />
          </div>
        ) : null}
      </header>

      {/* Abas de página (full-bleed) — só com dados carregados (dependem das capabilities) */}
      {overview ? (
        <SectionNav active={section} onSelect={setSection} capabilities={overview.capabilities} />
      ) : null}

      <div className="cdash-scroll">
        <div className="cdash-content">
          {isLoading ? (
            <div className="cdash-empty" data-testid="cdash-loading">
              <div className="cdash-empty-t">Carregando dashboard…</div>
            </div>
          ) : isError || !overview ? (
            <div className="cdash-empty" data-testid="cdash-error">
              <div className="cdash-empty-t">Não foi possível carregar o dashboard</div>
              <div className="cdash-empty-d">Verifique o canal e tente novamente.</div>
            </div>
          ) : (
            <>
              {section === 'overview' && (
                <>
                  <div className="cdash-sec first">
                    <ProfileHeader channel={overview.channel} />
                  </div>
                  {hasInsights ? (
                    <>
                      <div className="cdash-sec">
                        <span className="cdash-mlabel">Resumo</span>
                        <KpiRow latest={latest} previous={previous} />
                      </div>
                      {metrics.length > 0 && (
                        <div className="cdash-sec">
                          <ReachChartCard data={metrics} />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="cdash-sec">
                      <div className="cdash-empty" data-testid="cdash-no-insights">
                        <div className="cdash-empty-t">Métricas não disponíveis para este canal</div>
                        <div className="cdash-empty-d">Indicadores de alcance e seguidores existem apenas para canais sociais (Instagram). Veja o estado de conexão na aba Sync.</div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {section === 'metrics' && (
                <>
                  <div className="cdash-sec first">
                    <span className="cdash-mlabel">Indicadores</span>
                    <KpiRow latest={latest} previous={previous} />
                  </div>
                  {metrics.length > 0 ? (
                    <div className="cdash-sec">
                      <ReachChartCard data={metrics} />
                    </div>
                  ) : (
                    <div className="cdash-sec">
                      <div className="cdash-empty" data-testid="cdash-metrics-empty">
                        <div className="cdash-empty-t">Sem histórico de métricas ainda</div>
                        <div className="cdash-empty-d">Os dados aparecem após o primeiro sync com insights.</div>
                      </div>
                    </div>
                  )}
                  {hasAudience && (
                    <div className="cdash-sec">
                      <AudiencePanel audience={latest?.audience} />
                    </div>
                  )}
                </>
              )}

              {section === 'sync' && (
                <div className="cdash-sec first">
                  <SyncSection syncState={overview.syncState} />
                </div>
              )}
              {section === 'posts' && id && <PostsSection channelId={id} channel={overview.channel} />}
              {section === 'comments' && id && <CommentsSection channelId={id} />}
              {section === 'activity' && id && <ActivityTimeline channelId={id} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
